import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../../services/api.js";
import { API_BASE } from "../../config/api.js";
import { filterActiveAgents } from "../../lib/agentFilter.js";
import { buildTransferAgentQuery } from "../../lib/transferAgentIdentity.js";
import { isPlaceholder } from "../../lib/validation.js";

/***********************************
 * Constants
 ***********************************/
export const sindhGovDepartments = [
  "--Select--",
  "Law & Order",
  "Agri Water Issue",
  "Drinking Water Issue",
  "Land Dispute",
  "Land Enchrochment",
  "Elct_Gas Utilities",
  "Group Insaurance",
  "Illegal Construction",
  "Financial Support",
  "Sport Facility",
  "Minority Issues",
  "Right to Education",
  "Health Issue",
  "Other",
  "Federal government",
  "N/A",
  "Other Province",
];

/***********************************
 * Low-level Fetch Helpers
 ***********************************/
async function safeJsonFetch(url, init = {}) {
  const token = localStorage.getItem('token');
  if (token) {
    init.headers = { ...init.headers, Authorization: `Bearer ${token}` };
  }
  const res = await fetch(url, init);
  const text = await res.text();
  console.log("FETCH:", url, res.status);

  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    throw new Error(`Non-JSON (${res.status}): ${text}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Bad JSON: ${text}`);
  }
}

/***********************************
 * API Client (all endpoints in one place)
 ***********************************/
const Api = {
  // Lists
  getMFLTags: () => safeJsonFetch(`${API_BASE}/tags/mfl`),
  getWRLMPTags: () => safeJsonFetch(`${API_BASE}/tags/wrlmp`),
  getAgents: () => safeJsonFetch(`${API_BASE}/transfer/agents${buildTransferAgentQuery()}`),
  getOldTagOptions: () => safeJsonFetch(`${API_BASE}/tags/old`),
  getSmsTextOptions: () => safeJsonFetch(`${API_BASE}/sms-text-options`),

  // Category trees
  getSubcategories: (main) => {
    // Prefer CCID if we have it; otherwise fall back to category name
    const ccid = typeof main === "object" ? main?.id : main;
    const isId =
      ccid !== undefined && ccid !== null && ccid !== "" && !isNaN(ccid);
    const param = isId
      ? `ccid=${encodeURIComponent(ccid)}`
      : `category=${encodeURIComponent(
          typeof main === "object" ? main?.name || "" : main || ""
        )}`;
    return safeJsonFetch(`${API_BASE}/subcategories?${param}`);
  },
  getSubchildren: (subCategoryId) =>
    safeJsonFetch(`${API_BASE}/subchildren?subcategory_id=${subCategoryId}`),
  resolveChildByName: (name) =>
    safeJsonFetch(
      `${API_BASE}/subchild/by-name?name=${encodeURIComponent(name)}`
    ),

  // Saved selections for a complaint
  getSavedSelections: (queryId) =>
    safeJsonFetch(
      `${API_BASE}/complaints/${encodeURIComponent(queryId)}/selections`
    ).catch(() => null),

  // Update complaint
  updateComplaint: (queryId, payload) =>
    api.put(`/complaints/${queryId}`, payload),
  updateContact: (contactId, payload) =>
    api.put(`/contacts/${contactId}`, payload),
  createComplaint: (payload) => api.post(`/complaints`, payload),
};

/***********************************
 * Module-scope caches (survive remounts)
 ***********************************/
const selectionsCache = new Map(); // key: QueryID -> { OldTagIds, MFLIds, WRLMPIds, TagIds, MainTags, TagMFL, TagWRLMP }
const listCache = {
  mfl: null,
  wrlmp: null,
  old: null,
  agents: null,
  smsText: null,
};
const skey = (qid) => String(qid);

function getProceedMode(df) {
  const hasDept =
    df?.SindhGovDept &&
    df.SindhGovDept !== "--Select--" &&
    df.SindhGovDept.trim() !== "";
  const hasQuery = (df?.Query || "").trim().length > 0;
  const hasSolution = (df?.Solution || "").trim().length > 0;
  return hasDept && hasQuery && hasSolution ? "Complete" : "Initiated";
}
/***********************************
 * ID helpers + CSV helpers + mappers
 ***********************************/
const asKey = (x) => String(x);

// IDs -> NAMES
const namesFromIds = (ids = [], options = []) => {
  const byId = new Map(
    options.map((o) => [asKey(o.id), String(o.name ?? "").trim()])
  );
  const unique = Array.from(new Set((ids || []).map(asKey)));
  return unique
    .map((id) => byId.get(id) || "")
    .filter((n) => n.length > 0);
};

// CSV -> array of tokens (supports comma/semicolon)
const splitCsv = (val) =>
  String(val ?? "")
    .split(/[;,]/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0);

// NAMES -> IDs
const idsFromNames = (names = [], options = []) => {
  const byName = new Map(
    options.map((o) => [
      String(o.name ?? "").trim().toLowerCase(),
      asKey(o.id),
    ])
  );
  const uniq = Array.from(
    new Set((names || []).map((n) => String(n).trim().toLowerCase()))
  );
  return uniq.map((n) => byName.get(n)).filter(Boolean);
};

const namesFromSelectedIds = (ids = [], options = []) => {
  const byId = new Map(
    options.map((o) => [asKey(o.id), String(o.name ?? o.label ?? "").trim()])
  );
  return Array.from(new Set((ids || []).map(asKey)))
    .map((id) => byId.get(id) || id)
    .filter(Boolean);
};

const isGeneralInformationCategory = (detailForm = {}) =>
  String(
    detailForm.CategoryName ??
      detailForm.MainCategory ??
      detailForm.MainCat ??
      detailForm.Category ??
      detailForm.categoryName ??
      detailForm.mainCategory ??
      ""
  )
    .toLowerCase()
    .replace(/[\s_-]+/g, "") === "generalinformation";

const buildCompleteTagErrors = (detailForm = {}, oldTagOptions = []) => {
  if (isGeneralInformationCategory(detailForm)) {
    return {};
  }

  const oldIds = Array.isArray(detailForm.OldTagIds)
    ? detailForm.OldTagIds.map(asKey).filter(Boolean)
    : detailForm.OldTagId
    ? [asKey(detailForm.OldTagId)]
    : [];
  const mflIds = Array.isArray(detailForm.MFLIds)
    ? detailForm.MFLIds.map(asKey).filter(Boolean)
    : detailForm.MFL
    ? [asKey(detailForm.MFL)]
    : [];
  const wrlmpIds = Array.isArray(detailForm.WRLMPIds)
    ? detailForm.WRLMPIds.map(asKey).filter(Boolean)
    : detailForm.WRLMP
    ? [asKey(detailForm.WRLMP)]
    : [];
  const mainTagLabels = [
    ...namesFromSelectedIds(oldIds, oldTagOptions),
    ...splitCsv(detailForm.MainTags),
  ].map((value) => value.trim().toLowerCase());
  const requiresMfl = mainTagLabels.some((label) => label.includes("mfl"));
  const requiresWrlmp = mainTagLabels.some((label) => label.includes("wrlmp"));
  const errors = {};

  if (oldIds.length === 0 && mainTagLabels.length === 0) {
    errors.mainTags = "Main Tags is required before completing.";
  }
  if (requiresMfl && mflIds.length === 0) {
    errors.forMfl = "For MFL is required when Main Tags includes MFL.";
  }
  if (requiresWrlmp && wrlmpIds.length === 0) {
    errors.forWrlmp = "For WRLMP is required when Main Tags includes WRLMP.";
  }

  return errors;
};

/***********************************
 * Validation
 ***********************************/
function validate(detailForm, phoneNo, callerName, roleName) {
  const e = [];
  if (isPlaceholder(detailForm.ComplaintSource)) {
    e.push("Source can't be empty.");
  }
  if (!callerName) {
    e.push("Caller name is required.");
  }
  if (roleName === "FOE") {
    if (!detailForm.MainCat) e.push("Select main category.");
    if (!detailForm.Category) e.push("Select category (subcategory).");
  }
  return e;
}

/***********************************
 * Small UI helper: Checkbox Multi-Select (Bootstrap dropdown)
 ***********************************/
function MultiCheckSelect({
  label,
  placeholder = "--Select--",
  options = [],
  checkedIds = [],
  onToggle,
}) {
  const count = checkedIds.length;
  const summary = count === 0 ? placeholder : `${count} selected`;
  const checkedSet = new Set(checkedIds.map(asKey));
  return (
    <div className="dropdown w-100">
      <button
        className="btn btn-outline-secondary btn-sm dropdown-toggle w-100 text-start"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        <span className="me-2">{label}:</span>
        <span className="fw-semibold">{summary}</span>
      </button>
      <div
        className="dropdown-menu p-2"
        style={{ minWidth: 280, maxHeight: 260, overflowY: "auto" }}
      >
        {options.length === 0 && (
          <div className="text-muted small px-2">No options</div>
        )}
        {options.map((opt) => {
          const key = asKey(opt.id);
          return (
            <label
              key={key}
              className="dropdown-item d-flex align-items-center gap-2"
            >
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={checkedSet.has(key)}
                onChange={() => onToggle(key)}
              />
              <span>{opt.name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/***********************************
 * Component
 ***********************************/
export default function DetailTab({
  detailForm,
  setDetailForm,
  phoneNo = "",
  callerName = "",
  contactId = "",
  contactForm = null,
  roleName = "FO", // use "FOE" for stricter validation
  categories = [], // used for main categories dropdown
  // external tag buckets (if you render elsewhere, we keep these in sync on uncheckAll)
  mflTags = [],
  setMflTags = () => {},
  oldTags = [],
  setOldTags = () => {},
  wrlmpTags = [],
  setWrlmpTags = () => {},
}) {
  /********** State **********/
  const [errors, setErrors] = useState([]);
  const [tagFieldErrors, setTagFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [subCategories, setSubCategories] = useState([]); // [{id,name}]
  const [children, setChildren] = useState([]); // [{id,name}]
  const [tags, setTags] = useState([]); // reserved if you later add tag APIs
  const [faqs, setFaqs] = useState([]); // [{id,question}]
  const [faqEnabled, setFaqEnabled] = useState(false);

  const [, setTagOptions] = useState([]); // select options derived from tags
  const [mflList, setMflList] = useState(listCache.mfl || []); // MFL list
  const [wrlmpList, setWrlmpList] = useState(listCache.wrlmp || []); // WRLMP list
  const [agents, setAgents] = useState(listCache.agents || []);
  const [oldTagOptions, setOldTagOptions] = useState(listCache.old || []); // [{id,name, code/slug?}]
  const [smsTextOptions, setSmsTextOptions] = useState(listCache.smsText || []);

  // One-time/force fetch state across remounts
  const loadedSelectionsRef = useRef(false); // component-level
  const selectionsFetchStateRef = useRef("idle"); // "idle" | "loading" | "loaded"
  const catSelectRef = useRef(null);
  const [isNewQuery, setIsNewQuery] = useState(false);

  /***********************************
   * Loaders (with module-level caches)
   ***********************************/
  const loadMflWrlmp = useCallback(async () => {
    if (Array.isArray(listCache.mfl) && Array.isArray(listCache.wrlmp)) {
      setMflList(listCache.mfl);
      setWrlmpList(listCache.wrlmp);
      console.log("⚡ lists from cache: MFL/WRLMP");
      return;
    }
    try {
      const [mflJson, wrJson] = await Promise.all([
        Api.getMFLTags(),
        Api.getWRLMPTags(),
      ]);
      const mfl = mflJson?.success ? mflJson.data || [] : [];
      const wr = wrJson?.success ? wrJson.data || [] : [];
      listCache.mfl = mfl;
      listCache.wrlmp = wr;
      setMflList(mfl);
      setWrlmpList(wr);
      console.log("⬇️ lists fetched: MFL/WRLMP");
    } catch (e) {
      console.error("MFL/WRLMP load error", e);
      setMflList([]);
      setWrlmpList([]);
    }
  }, []);

  const loadAgents = useCallback(async () => {
    if (Array.isArray(listCache.agents)) {
      setAgents(listCache.agents);
      console.log("⚡ agents from cache");
      return;
    }
    try {
      const j = await Api.getAgents();
      const a = j?.success ? j.data || [] : [];
      const activeAgents = filterActiveAgents(a);
      listCache.agents = activeAgents;
      setAgents(activeAgents);
    } catch (e) {
      console.error("Agents load error", e);
    }
  }, []);

  const loadOldTags = useCallback(
    async () => {
      if (Array.isArray(listCache.old)) {
        setOldTagOptions(listCache.old);
        // Ensure OldTagIds is always an array for MultiCheckSelect (use legacy OldTagId if present)
        setDetailForm((prev) => ({
          ...prev,
          OldTagIds: Array.isArray(prev.OldTagIds)
            ? prev.OldTagIds
            : prev.OldTagId
            ? [String(prev.OldTagId)]
            : [],
        }));
        console.log("⚡ main tags from cache");
        return;
      }
      try {
        const json = await Api.getOldTagOptions();
        const opts = json?.success ? json.data || [] : [];
        listCache.old = opts;
        setOldTagOptions(opts);
        // Ensure OldTagIds is always an array for MultiCheckSelect (use legacy OldTagId if present)
        setDetailForm((prev) => ({
          ...prev,
          OldTagIds: Array.isArray(prev.OldTagIds)
            ? prev.OldTagIds
            : prev.OldTagId
            ? [String(prev.OldTagId)]
            : [],
        }));
        console.log("⬇️ main tags fetched");
      } catch (e) {
        console.error("Main tags load error", e);
        setOldTagOptions([]);
      }
    },
    [setDetailForm]
  );

  const loadSmsTextOptions = useCallback(async () => {
    if (Array.isArray(listCache.smsText)) {
      setSmsTextOptions(listCache.smsText);
      return;
    }

    try {
      const json = await Api.getSmsTextOptions();
      const opts = json?.success && Array.isArray(json.data)
        ? json.data
        : Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json)
        ? json
        : [];
      listCache.smsText = opts;
      setSmsTextOptions(opts);
    } catch (e) {
      console.error("SMS text options load error", e);
      setSmsTextOptions([]);
    }
  }, []);

  const pickFirstChildByName = useCallback(
    async (subCatId) => {
      try {
        const j = await Api.getSubchildren(subCatId);
        const kids = j?.success ? j.data || [] : [];
        setChildren(kids);

        if (kids.length > 0) {
          const firstName = kids[0].name;

          // set selected child name first (so UI updates)
          setDetailForm((prev) => ({
            ...prev,
            SubCatChild: firstName,
            ChildCategoryID: null, // force by-name resolution
            FAQ: "",
          }));

          // resolve ID via **by-name** (NOT by id)
          const r = await Api.resolveChildByName(firstName);
          const childId = r?.data?.id ?? r?.data?.SCCID ?? null;
          if (childId) {
            setDetailForm((prev) => ({ ...prev, ChildCategoryID: childId }));
          }
        } else {
          // no children for this subcategory
          setDetailForm((prev) => ({
            ...prev,
            SubCatChild: "",
            ChildCategoryID: null,
            FAQ: "",
          }));
        }
      } catch (err) {
        console.error("pickFirstChildByName error:", err);
        setChildren([]);
        setDetailForm((prev) => ({
          ...prev,
          SubCatChild: "",
          ChildCategoryID: null,
          FAQ: "",
        }));
      }
    },
    [setDetailForm]
  );

  // UPDATED: loadSubCats so it also loads children & auto-selects when needed
  const loadSubCats = useCallback(
    async (main) => {
      if (!main) {
        setSubCategories([]);
        setChildren([]);
        setDetailForm((prev) => ({
          ...prev,
          Category: "",
          CategoryID: null,
          SubCatChild: "",
          ChildCategoryID: null,
        }));
        return;
      }

      try {
        const json = await Api.getSubcategories(main);
        if (!json?.success) {
          setSubCategories([]);
          setChildren([]);
          return;
        }

        const data = Array.isArray(json.data) ? json.data : [];
        setSubCategories(data);

        // maintain CategoryID from backend if present
        setDetailForm((prev) => ({
          ...prev,
          CategoryID: prev.CategoryID ?? json.category?.id ?? null,
        }));

        // decide which subcategory to use
        let chosen = null;

        if (detailForm.Category) {
          chosen = data.find((d) => d.name === detailForm.Category) || null;
        }

        if (!chosen && data.length > 0) {
          chosen = data[0];
          setDetailForm((prev) => ({
            ...prev,
            Category: chosen.name,
            CategoryID: chosen.id,
          }));
        } else if (chosen) {
          setDetailForm((prev) => ({
            ...prev,
            CategoryID: chosen.id,
          }));
        }

        // load children for that subcategory and auto-pick first
        if (chosen?.id) {
          await pickFirstChildByName(chosen.id);
        } else {
          setChildren([]);
          setDetailForm((prev) => ({
            ...prev,
            SubCatChild: "",
            ChildCategoryID: null,
          }));
        }
      } catch (err) {
        console.error("Subcategories error:", err);
        setSubCategories([]);
        setChildren([]);
      }
    },
    [detailForm.Category, setDetailForm, pickFirstChildByName]
  );

  /***********************************
   * Saved selections loader with cache
   ***********************************/
  const applySelectionsToState = useCallback(
    (sel) => {
      if (!sel) return;
      setDetailForm((prev) => ({
        ...prev,
        PreselectedTagIds: Array.isArray(sel.TagIds)
          ? sel.TagIds.map(asKey)
          : prev.PreselectedTagIds,
        MFLIds: Array.isArray(sel.MFLIds)
          ? sel.MFLIds.map(asKey)
          : prev.MFLIds,
        WRLMPIds: Array.isArray(sel.WRLMPIds)
          ? sel.WRLMPIds.map(asKey)
          : prev.WRLMPIds,
        OldTagIds: Array.isArray(sel.OldTagIds)
          ? sel.OldTagIds.map(asKey)
          : prev.OldTagIds,
      }));
    },
    [setDetailForm]
  );

  const loadSavedSelectionsIfAny = useCallback(
    async (force = false) => {
      const qid = detailForm?.QueryID;
      if (!qid) return;
      const k = skey(qid);

      // 1) Short-circuit from cache when not forced
      if (!force && selectionsCache.has(k)) {
        console.log("♻️ Using cached selections for", k, selectionsCache.get(k));
        applySelectionsToState(selectionsCache.get(k));
        return;
      }

      // 2) Fetch from server
      let server = null;
      try {
        server = await Api.getSavedSelections(qid);
      } catch (e) {
        server = null;
      }
      const src = server?.data ?? server ?? {};
      const mainTagsValue = src.MainTags ?? src.mainTags ?? src.maintags ?? "";
      const tagMflValue = src.TagMFL ?? src.tagMFL ?? src.tagMfl ?? src.tagmfl ?? "";
      const tagWrlmpValue = src.TagWRLMP ?? src.tagWRLMP ?? src.tagWrlmp ?? src.tagwrlmp ?? "";

      // Prefer arrays if present (backward-compat)
      let TagIdsArr = Array.isArray(src.TagIds ?? src.tagIds ?? src.tagids) ? (src.TagIds ?? src.tagIds ?? src.tagids) : [];
      let MFLIdsArr = Array.isArray(src.MFLIds ?? src.mflIds ?? src.mflids) ? (src.MFLIds ?? src.mflIds ?? src.mflids) : [];
      let WRIdsArr = Array.isArray(src.WRLMPIds ?? src.wrlmpIds ?? src.wrlmpids) ? (src.WRLMPIds ?? src.wrlmpIds ?? src.wrlmpids) : [];
      let OldIdsArr = Array.isArray(src.OldTagIds ?? src.oldTagIds ?? src.oldtagids) ? (src.OldTagIds ?? src.oldTagIds ?? src.oldtagids) : [];

      // If arrays missing but NAMES CSV present
      if (OldIdsArr.length === 0 && mainTagsValue.length > 0) {
        const names = splitCsv(mainTagsValue);
        OldIdsArr = idsFromNames(names, listCache.old || oldTagOptions);
      }
      if (MFLIdsArr.length === 0 && tagMflValue.length > 0) {
        const names = splitCsv(tagMflValue);
        MFLIdsArr = idsFromNames(names, listCache.mfl || mflList);
      }
      if (WRIdsArr.length === 0 && tagWrlmpValue.length > 0) {
        const names = splitCsv(tagWrlmpValue);
        WRIdsArr = idsFromNames(names, listCache.wrlmp || wrlmpList);
      }

      // Also handle CSV-of-IDs (just in case)
      const csvIdsToArr = (v) => splitCsv(v).map(asKey);
      const tagIdsValue = src.TagIds ?? src.tagIds ?? src.tagids;
      const mflIdsValue = src.MFLIds ?? src.mflIds ?? src.mflids;
      const wrlmpIdsValue = src.WRLMPIds ?? src.wrlmpIds ?? src.wrlmpids;
      const oldTagIdsValue = src.OldTagIds ?? src.oldTagIds ?? src.oldtagids;
      if (TagIdsArr.length === 0 && typeof tagIdsValue === "string")
        TagIdsArr = csvIdsToArr(tagIdsValue);
      if (MFLIdsArr.length === 0 && typeof mflIdsValue === "string")
        MFLIdsArr = csvIdsToArr(mflIdsValue);
      if (WRIdsArr.length === 0 && typeof wrlmpIdsValue === "string")
        WRIdsArr = csvIdsToArr(wrlmpIdsValue);
      if (OldIdsArr.length === 0 && typeof oldTagIdsValue === "string")
        OldIdsArr = csvIdsToArr(oldTagIdsValue);

      // 3) Normalize + cache
      const normalized = {
        TagIds: TagIdsArr.map(asKey),
        MFLIds: MFLIdsArr.map(asKey),
        WRLMPIds: WRIdsArr.map(asKey),
        OldTagIds: OldIdsArr.map(asKey),
        MainTags: mainTagsValue,
        TagMFL: tagMflValue,
        TagWRLMP: tagWrlmpValue,
      };
      selectionsCache.set(k, normalized);
      console.log("⬇️ Fetched & cached selections for", k, normalized);

      // 4) Apply to state
      applySelectionsToState(normalized);
    },
    [
      detailForm?.QueryID,
      applySelectionsToState,
      oldTagOptions,
      mflList,
      wrlmpList,
    ]
  );

  /***********************************
   * Fetch-once wrapper: initial load only; force after save
   ***********************************/
  const fetchSelectionsOnce = useCallback(
    async (force = false) => {
      const qid = detailForm?.QueryID;
      if (!qid) return;

      // Concurrency + once guards (component-level)
      if (!force) {
        if (loadedSelectionsRef.current) return; // already applied once in this mount
        if (selectionsFetchStateRef.current !== "idle") return; // in-flight or already loaded
      } else {
        // force: allow only if currently not loading
        if (selectionsFetchStateRef.current === "loading") return;
      }

      selectionsFetchStateRef.current = "loading";
      try {
        const k = skey(qid);
        console.log(
          force
            ? "🔁 Forcing /selections (and cache refresh) after save"
            : selectionsCache.has(k)
            ? "♻️ Using cache on mount (no network)"
            : "🟢 One-time /selections fetch on first mount"
        );
        await loadSavedSelectionsIfAny(force);
        loadedSelectionsRef.current = true;
        selectionsFetchStateRef.current = "loaded";
      } catch (e) {
        console.error("selections fetch failed", e);
        selectionsFetchStateRef.current = "loaded"; // avoid loops
      }
    },
    [detailForm?.QueryID, loadSavedSelectionsIfAny]
  );

  /***********************************
   * Effects (call loaders)
   ***********************************/
  useEffect(() => {
    loadMflWrlmp();
    loadAgents();
    loadOldTags();
    loadSmsTextOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset fetch guards when QueryID changes
  useEffect(() => {
    loadedSelectionsRef.current = false;
    selectionsFetchStateRef.current = "idle";
  }, [detailForm?.QueryID]);

  useEffect(() => {
    loadSubCats(detailForm?.MainCat);
  }, [detailForm?.MainCat, loadSubCats]);

  // Only once when lists are available (first mount or after remount)
  useEffect(() => {
    if (!detailForm?.QueryID) return;

    const listsReady =
      (oldTagOptions.length > 0 ||
        (listCache.old && listCache.old.length > 0)) ||
      (mflList.length > 0 || (listCache.mfl && listCache.mfl.length > 0)) ||
      (wrlmpList.length > 0 || (listCache.wrlmp && listCache.wrlmp.length > 0));
    if (!listsReady) return;

    fetchSelectionsOnce(false);
  }, [
    detailForm?.QueryID,
    oldTagOptions.length,
    mflList.length,
    wrlmpList.length,
    fetchSelectionsOnce,
  ]);

  // Rehydrate from cached names if IDs were empty when lists weren't ready
  useEffect(() => {
    const qid = detailForm?.QueryID;
    if (!qid) return;
    const k = skey(qid);
    const cached = selectionsCache.get(k);
    if (!cached) return;

    const needOld =
      (!Array.isArray(detailForm.OldTagIds) ||
        detailForm.OldTagIds.length === 0) &&
      (cached.MainTags || "").length > 0 &&
      oldTagOptions.length > 0;
    const needMfl =
      (!Array.isArray(detailForm.MFLIds) || detailForm.MFLIds.length === 0) &&
      (cached.TagMFL || "").length > 0 &&
      mflList.length > 0;
    const needWr =
      (!Array.isArray(detailForm.WRLMPIds) ||
        detailForm.WRLMPIds.length === 0) &&
      (cached.TagWRLMP || "").length > 0 &&
      wrlmpList.length > 0;
    if (!needOld && !needMfl && !needWr) return;

    const next = { ...cached };
    if (needOld)
      next.OldTagIds = idsFromNames(
        splitCsv(cached.MainTags),
        oldTagOptions
      ).map(asKey);
    if (needMfl)
      next.MFLIds = idsFromNames(splitCsv(cached.TagMFL), mflList).map(asKey);
    if (needWr)
      next.WRLMPIds = idsFromNames(splitCsv(cached.TagWRLMP), wrlmpList).map(
        asKey
      );

    selectionsCache.set(k, next);
    applySelectionsToState(next);
    console.log("🔁 Rehydrated selections from names after lists ready", next);
  }, [
    applySelectionsToState,
    detailForm?.MFLIds,
    detailForm?.OldTagIds,
    detailForm?.QueryID,
    detailForm?.WRLMPIds,
    mflList,
    oldTagOptions,
    wrlmpList,
  ]);

  /***********************************
   * Handlers (UI -> state)
   ***********************************/
  function handleInputChange(e) {
    const { name, value, type, checked } = e.target;
    setDetailForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  function handleMainCatChange(e) {
    const main = e.target.value;
    setDetailForm((prev) => ({
      ...prev,
      MainCat: main,
      Category: "",
      CategoryID: null,
      SubCatChild: "",
      ChildCategoryID: null,
      FAQ: "",
    }));
    setSubCategories([]);
    setChildren([]);
    setTags([]);
    setTagOptions([]);
    setFaqs([]);
    setFaqEnabled(false);
  }

  async function handleCategoryChange(e) {
    const picked = e.target.value;
    const found = subCategories.find((s) => s.name === picked);

    // reset dependent fields first
    setDetailForm((prev) => ({
      ...prev,
      Category: picked,
      CategoryID: found?.id ?? null,
      SubCatChild: "",
      ChildCategoryID: null,
      FAQ: "",
    }));
    setChildren([]);
    setTags([]);
    setTagOptions([]);
    setFaqs([]);
    setFaqEnabled(false);

    // load children & auto-pick first, then resolve **by-name**
    if (found?.id) {
      await pickFirstChildByName(found.id);
    }
  }

  async function handleChildChange(e) {
    const pickedName = e.target.value;

    // set name first so UI reflects change
    setDetailForm((prev) => ({
      ...prev,
      SubCatChild: pickedName,
      ChildCategoryID: null, // force by-name resolve
      FAQ: "",
    }));

    try {
      const r = await Api.resolveChildByName(pickedName);
      const childId = r?.data?.id ?? r?.data?.SCCID ?? null;
      if (childId) {
        setDetailForm((prev) => ({ ...prev, ChildCategoryID: childId }));
      }
    } catch (err) {
      console.error("handleChildChange by-name resolve failed:", err);
    }
  }

  function uncheckAll() {
    setTags((prev) => prev.map((t) => ({ ...t, checked: false })));
    setMflTags((prev) => prev.map((x) => ({ ...x, checked: false })));
    setOldTags((prev) => prev.map((x) => ({ ...x, checked: false })));
    setWrlmpTags((prev) => prev.map((x) => ({ ...x, checked: false })));
    setTagFieldErrors({});
    setDetailForm((prev) => ({
      ...prev,
      MFLIds: [],
      WRLMPIds: [],
      OldTagIds: [],
    }));
  }

  // toggle helper for multi-select arrays kept in detailForm (string-normalized)
  function clearTagFieldError(field) {
    setTagFieldErrors((prev) => {
      const next = { ...prev };
      if (field === "OldTagIds") {
        delete next.mainTags;
        delete next.forMfl;
        delete next.forWrlmp;
      }
      if (field === "MFLIds") delete next.forMfl;
      if (field === "WRLMPIds") delete next.forWrlmp;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }

  function toggleIdInArray(field, id) {
    const key = asKey(id);
    clearTagFieldError(field);
    setDetailForm((prev) => {
      const arr = Array.isArray(prev[field]) ? prev[field].map(asKey) : [];
      const exists = arr.includes(key);
      const next = exists ? arr.filter((x) => x !== key) : [...arr, key];
      return { ...prev, [field]: next };
    });
  }

  /***********************************
   * Build payload (send NAMES for MainTags/TagMFL/TagWRLMP)
   ***********************************/
  function buildPayload(detailForm, roleName, phoneNo, callerName) {
    const ADRQuery = detailForm.ADRQuery ? "ADR Query" : " ";
    const COVID = detailForm.COVID ? "COVID-19" : " ";
    const AgeAboveFifty = detailForm.AgeAboveFifty ? "Age > 50" : " ";
    const Domesticviolence = detailForm.Domesticviolence
      ? "Gender Base Violence"
      : " ";
    const WomenRelated = detailForm.WomenRelated ? "Women Related" : " ";
    const WomenProperty = detailForm.WomenProperty ? "Women Property" : " ";
    const ShortQuery = detailForm.ShortQuery ? "1" : "0";

    // current selected IDs (arrays)
    const mflIds = Array.isArray(detailForm.MFLIds) ? detailForm.MFLIds : [];
    const wrlmpIds = Array.isArray(detailForm.WRLMPIds)
      ? detailForm.WRLMPIds
      : [];
    const oldIds = Array.isArray(detailForm.OldTagIds)
      ? detailForm.OldTagIds
      : [];

    // convert IDs → NAMES for saving (comma-separated values)
    const oldNamesCsv = namesFromIds(
      oldIds,
      listCache.old || oldTagOptions
    ).join(",");
    const mflNamesCsv = namesFromIds(
      mflIds,
      listCache.mfl || mflList
    ).join(",");
    const wrlmpNamesCsv = namesFromIds(
      wrlmpIds,
      listCache.wrlmp || wrlmpList
    ).join(",");

    return {
      ModifiedBy: detailForm.ModifiedBy || detailForm.Agentid || "",
      ContactPerson: callerName || detailForm.ContactPerson || "",
      ContactNumber: phoneNo || detailForm.ContactNumber || "",

      CategoryName: detailForm.MainCat || "", // main
      SubCategoryName: detailForm.Category || "", // subcategory (UI "Category")
      SubCatChild: detailForm.SubCatChild || "", // child label (optional)

      ComplaintSource: detailForm.ComplaintSource || "",
      Priority: detailForm.Priority || "",

      SMSText: detailForm.SMSText || "",
      Query: detailForm.Query || "",
      Solution: detailForm.Solution || "",

      AdditionalInfo1: ADRQuery,
      COVID,
      AgeAboveFifty,
      Domesticviolence,
      AdditionalInfo2: WomenRelated,
      WomenProperty,
      FurtherHelp: detailForm.FurtherHelp || "",
      SindhGovDept: detailForm.SindhGovDept || "",

      // send VALUES (names) comma-separated
      MainTags: oldNamesCsv, // Main tags (names)
      TagMFL: mflNamesCsv, // MFL (names)
      TagWRLMP: wrlmpNamesCsv, // WRLMP (names)

      // legacy single fields
      OldTagId: oldIds[0] ?? detailForm.OldTagId ?? "",
      MFL: mflIds[0] ?? detailForm.MFL ?? "",
      WRLMP: wrlmpIds[0] ?? detailForm.WRLMP ?? "",

      AdditionalInfo3: roleName === "FOE" ? "1" : detailForm.AdditionalInfo3 || "0",
      ShortQuery,

      CategoryID: detailForm.CategoryID || null, // subcategory id
      SubCategoryID: detailForm.SubCategoryID || null,
      ChildCategoryID: detailForm.ChildCategoryID || null,
    };
  }

function buildCreatePayload(
  detailForm,
  { oldTagOptions, mflList, wrlmpList },
  { phoneNo, callerName },
  mode = "Initiated"
) {
    // normalize ids
    const oldIds = Array.isArray(detailForm.OldTagIds)
      ? detailForm.OldTagIds
      : [];
    const mflIds = Array.isArray(detailForm.MFLIds)
      ? detailForm.MFLIds
      : [];
    const wrlmpIds = Array.isArray(detailForm.WRLMPIds)
      ? detailForm.WRLMPIds
      : [];

    // convert IDs → NAMES (comma-separated) like WinForms did (Items[i].Text)
    const MainTags = namesFromIds(oldIds, oldTagOptions).join(", ");
    const TagMFL = namesFromIds(mflIds, mflList).join(", ");
    const TagWRLMP = namesFromIds(wrlmpIds, wrlmpList).join(", ");

    // flags → strings
    const AdditionalInfo1 = detailForm.ADRQuery ? "ADR Query" : " ";
    const COVID = detailForm.COVID ? "COVID-19" : " ";
    const AdditionalInfo2 = detailForm.WomenRelated ? "Women Related" : " ";
    const AgeAboveFifty = detailForm.AgeAboveFifty ? "Age > 50" : " ";
    const Domesticviolence = detailForm.Domesticviolence
      ? "Gender Base Violence"
      : " ";
    const WomenProperty = detailForm.WomenProperty ? "Women Property" : " ";

    // Category IDs: set to 0 if “--Select--”
    const categoryId = detailForm.CategoryID ?? 0;
    const subCategoryId = detailForm.SubCategoryID ?? 0;
    const childCatId = detailForm.ChildCategoryID ?? 0;

    return {
      // Essentials
      ContactPerson: callerName || detailForm.ContactPerson || "",
      ContactNumber: phoneNo || detailForm.ContactNumber || "",

      CategoryName: detailForm.MainCat || "",
      SubCategoryName: detailForm.Category || "",
      ComplaintSource: detailForm.ComplaintSource || "",
      Priority: detailForm.Priority || "Normal",

      // Texts
      SMSText: detailForm.SMSText || "",
      Query: detailForm.Query || "",
      Solution: detailForm.Solution || "",

      // Flags / extra info
      AdditionalInfo1,
      AdditionalInfo2,
      COVID,
      AgeAboveFifty,
      Domesticviolence,
      WomenProperty,
      FurtherHelp: detailForm.FurtherHelp || "",
      SindhGovDept: detailForm.SindhGovDept || "",

      // child info
      AdditionalInfo4: childCatId, // childCatID
      AdditionalInfo5: detailForm.SubCatChild || "", // childCatName

      // IDs
      CategoryID: categoryId,
      SubCategoryID: subCategoryId,
      ChildCategoryID: childCatId,

    // Job status on create
    JobStatus: mode === "Complete" ? "Complete" : "Initiated",

      // CSV tags (names)
      MainTags,
      TagMFL,
      TagWRLMP,

      CreatedBy: detailForm.Created || "",
    };
  }

  /***********************************
   * Refresh after Save
   ***********************************/
  async function refreshSelectionsAfterSave() {
    // Freeze current (saved) tag ids into PreselectedTagIds
    const selectedTagIds = tags.filter((t) => t.checked).map((t) => t.id);
    setDetailForm((prev) => ({
      ...prev,
      PreselectedTagIds: selectedTagIds.map(asKey),
    }));

    // If later you re-enable tags/FAQ API, fetch here
    const cid = detailForm?.ChildCategoryID;
    const qid = detailForm?.QueryID || "";
    if (cid && qid) {
      // loadTagsAndFaqs(cid, qid)
    }
  }

  const toStr = (value) =>
    value === null || value === undefined ? "" : String(value).trim();

  const resolveContactId = () => {
    const fromProps = contactId || contactForm?.id || contactForm?.ID || "";
    const fromDetail =
      detailForm?.CID || detailForm?.ContactID || detailForm?.ContactId || "";
    return toStr(fromProps || fromDetail);
  };

  const buildContactPayload = () => {
    const form = contactForm || {};
    return {
      Name: toStr(form.Name || form.name || callerName),
      CellNo: toStr(form.CellNo || form.cellNo || form.callback1),
      Gender: toStr(form.Gender || form.gender),
      CNIC: toStr(form.CNIC || form.cnic),
      Age: toStr(form.Age || form.age),
      Province: toStr(
        form.AdditionalInfo3 || form.Province || form.province
      ),
      Country: toStr(
        form.AdditionalInfo4 || form.Country || form.country
      ),
      District: toStr(
        form.AdditionalInfo2 || form.District || form.district
      ),
      City: toStr(form.City || form.city),
      HeardFrom: toStr(
        form.AdditionalInfo5 || form.HeardFrom || form.heardFrom
      ),
      PhoneNo: toStr(form.PhoneNo || form.phoneNo || form.cli || phoneNo),
      FatherName: toStr(form.FatherName || form.fatherHusbandName),
      Religion: toStr(
        form.AdditionalInfo6 || form.Religion || form.religion
      ),
      Address: toStr(form.Address || form.address),
      EmailAddress: toStr(form.EmailAddress),
      OfficeName: toStr(form.OfficeName || form.office),
      OfficeAddress: toStr(form.OfficeAddress),
      OfficePhoneNo: toStr(form.OfficePhoneNo),
      Notes: toStr(form.Notes),
      CallbackTwo: toStr(
        form.AdditionalInfo10 || form.CallbackTwo || form.callback2
      ),
      ContactRemarks: toStr(
        form.AdditionalInfo9 || form.ContactRemarks || form.remarks
      ),
      MaritalStatus: toStr(
        form.AdditionalInfo8 || form.MaritalStatus || form.maritalStatus
      ),
      Salary: toStr(form.AdditionalInfo1 || form.Salary || form.salary),
    };
  };

  /***********************************
   * Save Flow
   ***********************************/
  async function handleSaveDetails() {
    const v = validate(detailForm, phoneNo, callerName, roleName);
    if (v.length) {
      setErrors(v);
      return;
    }
    setErrors([]);

    if (!detailForm.QueryID) {
      setErrors(["Missing QueryID (ComplaintID)."]);
      return;
    }
    const resolvedContactId = resolveContactId();
    if (!resolvedContactId) {
      setErrors(["Missing Contact ID (CID)."]);
      return;
    }

    let stage = "contact";
    try {
      setSaving(true);
      await Api.updateContact(resolvedContactId, buildContactPayload());

      stage = "complaint";
      const payload = buildPayload(
        detailForm,
        roleName,
        phoneNo,
        callerName
      );

      console.log(
        "🔗 Update URL:",
        `${API_BASE}/complaints/${detailForm.QueryID}`
      );
      console.log("📦 Payload names:", {
        MainTags: payload.MainTags,
        TagMFL: payload.TagMFL,
        TagWRLMP: payload.TagWRLMP,
      });

      const res = await Api.updateComplaint(detailForm.QueryID, payload);

      const updated = res?.data?.data;
      if (updated && typeof updated === "object") {
        setDetailForm((prev) => ({
          ...prev,
        }));
      }

      const k = skey(detailForm.QueryID);
      selectionsCache.delete(k);
      loadedSelectionsRef.current = false;
      selectionsFetchStateRef.current = "idle";

      await fetchSelectionsOnce(true);
      await refreshSelectionsAfterSave();
      alert("Query updated successfully.");
    } catch (err) {
      console.error(err);
      const failedContact = stage === "contact";
      setErrors([
        failedContact ? "Failed to update contact." : "Failed to update query.",
      ]);
      alert(failedContact ? "Failed to update contact." : "Failed to update query.");
    } finally {
      setSaving(false);
    }
  }

  const handleProceedClick = useCallback(
    async (mode /* "Initiated" | "Complete" */) => {
      // 1) Generic source validation
      if (isPlaceholder(detailForm.ComplaintSource)) {
        setErrors(["Please select Source."]);
        return;
      }

      // 2) Mode-specific validations
      if (mode === "Complete") {
        if (isPlaceholder(detailForm.SindhGovDept)) {
          setErrors(["Sindh Government department can't be empty!"]);
          return;
        }
        const missing = [];
        if (!detailForm.Query || detailForm.Query.trim() === "")
          missing.push("Query");
        if (!detailForm.Solution || detailForm.Solution.trim() === "")
          missing.push("Solution");
        if (missing.length) {
          setErrors([`Please fill: ${missing.join(", ")}`]);
          return;
        }
        const completeTagErrors = buildCompleteTagErrors(
          detailForm,
          oldTagOptions
        );
        setTagFieldErrors(completeTagErrors);
        if (Object.keys(completeTagErrors).length > 0) {
          return;
        }
      }

      const phone = (detailForm.ContactNumber || "").trim();
      const name = (detailForm.ContactPerson || "").trim();

      if (mode === "Initiated") {
        if (!phone) {
          setErrors(["Phone No. can't be empty"]);
          return;
        }
        if (!name) {
          setErrors(["Please enter caller name."]);
          return;
        }
      }

      const formForCreate = {
        ...detailForm,
        ComplaintSource: isPlaceholder(detailForm.ComplaintSource)
          ? ""
          : detailForm.ComplaintSource,
        SindhGovDept: isPlaceholder(detailForm.SindhGovDept)
          ? ""
          : detailForm.SindhGovDept,
      };

      if (mode === "Initiated") {
        const hasCategory = !isPlaceholder(detailForm.Category);
        const hasChild = !isPlaceholder(detailForm.SubCatChild);
        formForCreate.CategoryID = hasCategory ? detailForm.CategoryID ?? 0 : 0;
        formForCreate.SubCategoryID = hasCategory
          ? detailForm.SubCategoryID ?? 0
          : 0;
        formForCreate.ChildCategoryID = hasChild
          ? detailForm.ChildCategoryID ?? 0
          : 0;

        setDetailForm((prev) => ({
          ...prev,
          CategoryID: formForCreate.CategoryID,
          SubCategoryID: formForCreate.SubCategoryID,
          ChildCategoryID: formForCreate.ChildCategoryID,
        }));
      }

      const payload = buildCreatePayload(
        formForCreate,
        { oldTagOptions, mflList, wrlmpList },
        { phoneNo, callerName },
        mode
      );

      try {
        setSaving(true);
        console.log("🟢 Creating complaint with payload:", payload);
        const res = await Api.createComplaint(payload);

        const newId =
          res?.data?.id ??
          res?.data?.LastQID ??
          res?.data?.ComplaintID ??
          res?.data?.data?.id ??
          res?.data?.data?.ComplaintID;

        if (!newId) {
          console.warn("Create returned no id; raw:", res?.data);
          alert("Created, but no id returned.");
          return;
        }

        setDetailForm((prev) => ({
          ...prev,
          QueryID: String(newId),
          JobStatus: mode === "Complete" ? "Complete" : "Initiated",
          CreatedOn: prev.CreatedOn || new Date().toISOString().slice(0, 10),
          Created: prev.Created || "",
        }));

        const k = String(newId);
        selectionsCache.delete(k);
        loadedSelectionsRef.current = false;
        selectionsFetchStateRef.current = "idle";
        await fetchSelectionsOnce(true);

        alert(`Query created. ID: ${newId}`);
      } catch (err) {
        console.error("Create failed", err);
        alert("Failed to create query.");
      } finally {
        setSaving(false);
      }
    },
    [
      detailForm,
      oldTagOptions,
      mflList,
      wrlmpList,
      phoneNo,
      callerName,
      setDetailForm,
      fetchSelectionsOnce,
    ]
  );

  function handleNewQuery() {
    try {
      setErrors([]);
      setSaving(false);

      uncheckAll();

      setSubCategories([]);
      setChildren([]);
      setTags([]);
      setTagOptions([]);
      setFaqs([]);
      setFaqEnabled(false);

      loadedSelectionsRef.current = false;
      selectionsFetchStateRef.current = "idle";

      setDetailForm({
        QueryID: "",
        JobStatus: "Initiated",
        Created: "",
        Modified: "",
        CreatedOn: "",
        ModifiedOn: "",

        MainCat: "",
        Category: "",
        CategoryID: 0,
        SubCatChild: "",
        ChildCategoryID: 0,
        SubCategoryID: 0,

        Query: "",
        Solution: "",
        SMSText: "",
        FurtherHelp: "",

        ADRQuery: false,
        COVID: false,
        WomenRelated: false,
        AgeAboveFifty: false,
        Domesticviolence: false,
        WomenProperty: false,
        ShortQuery: false,
        IsReferred: false,
        SelectedAgent: "",

        Priority: "Normal",
        ComplaintSource: "",
        SindhGovDept: sindhGovDepartments[0] ?? "--Select--",

        ContactPerson: "",
        ContactNumber: "",

        OldTagIds: [],
        MFLIds: [],
        WRLMPIds: [],

        OldTagId: "",
        MFL: "",
        WRLMP: "",
        AdditionalInfo3: "0",
      });

      setIsNewQuery(true);
      setTimeout(() => catSelectRef.current?.focus(), 0);
    } catch (err) {
      console.error("handleNewQuery failed:", err);
      alert(String(err?.message || err));
    }
  }

  async function handleFaqChange(e) {
    const selectedId = e.target.value;
    setDetailForm((prev) => ({ ...prev, FAQ: selectedId }));
    if (!selectedId) return;
    try {
      const faqHeaders = {};
      const faqToken = localStorage.getItem('token');
      if (faqToken) faqHeaders.Authorization = `Bearer ${faqToken}`;
      const res = await fetch(
        `${API_BASE}/questions-faq-detail?QuestionNoId=${encodeURIComponent(selectedId)}`,
        { headers: faqHeaders }
      );
      const json = await res.json();
      const data = json?.data ?? json;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return;
      const question = String(
        row.questiontitle ?? row.QuestionTitle ?? row.question ?? row.title ?? ""
      ).trim();
      const answer = String(
        row.answertitle ?? row.AnswerTitle ?? row.answer ?? row.Answer ?? ""
      ).trim();
      setDetailForm((prev) => ({
        ...prev,
        ...(question && { Query: question }),
        ...(answer && { Solution: answer }),
      }));
    } catch (err) {
      console.error("FAQ detail load error:", err);
    }
  }

  const canComplete = getProceedMode(detailForm) === "Complete";
  const smsTextDropdownOptions = (() => {
    const values = smsTextOptions
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.value || item?.Value || item?.label || item?.name || "";
      })
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    const currentValue = String(detailForm.SMSText || "").trim();
    return currentValue && !values.includes(currentValue)
      ? [currentValue, ...values]
      : values;
  })();

  /***********************************
   * Render
   ***********************************/
  return (
    <div className="container-fluid p-2">
      <div className="card p-3 shadow-sm rounded-3">
        <h5 className="mb-3">Query Details</h5>

        {errors.length > 0 && (
          <div className="alert alert-danger py-2">
            {errors.map((x, i) => (
              <div key={i}>{x}</div>
            ))}
          </div>
        )}

        <div className="row g-1">
          {/* IDs / meta */}
          <div className="col-md-2">
            <label className="form-label">Query ID</label>
            <input
              name="QueryID"
              value={detailForm.QueryID || ""}
              onChange={handleInputChange}
              className="form-control form-control-sm"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <input
              className="form-control form-control-sm"
              value={detailForm.JobStatus || "Initiated"}
              disabled
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Launched By</label>
            <input
              name="Created"
              value={detailForm.Created || ""}
              onChange={handleInputChange}
              className="form-control form-control-sm"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Accepted By</label>
            <input
              name="Modified"
              value={detailForm.Modified || ""}
              onChange={handleInputChange}
              className="form-control form-control-sm"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Launched On</label>
            <input
              type="date"
              name="CreatedOn"
              value={detailForm.CreatedOn || ""}
              onChange={handleInputChange}
              className="form-control form-control-sm"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Accepted On</label>
            <input
              type="date"
              name="ModifiedOn"
              value={detailForm.ModifiedOn || ""}
              onChange={handleInputChange}
              className="form-control form-control-sm"
            />
          </div>

          {/* Main Category */}
          <div className="col-md-3">
            <label className="form-label">Main Category</label>
            <select
              ref={catSelectRef}
              name="MainCat"
              value={detailForm.MainCat || ""}
              onChange={handleMainCatChange}
              className="form-select form-select-sm"
            >
              <option value="">--Select--</option>
              {categories.map((c) => (
                <option key={c.id ?? c.ID ?? c.name} value={c.name ?? c.CCDescription}>
                  {c.name ?? c.CCDescription}
                </option>
              ))}
            </select>
          </div>

          {/* Category (UI) = Subcategory (DB) */}
          <div className="col-md-3">
            <label className="form-label">Category (Subcategory)</label>
            <select
              className="form-select form-select-sm"
              value={detailForm.Category || ""}
              onChange={handleCategoryChange}
            >
              {subCategories.length === 0 && (
                <option value="">-- No Subcategories --</option>
              )}
              {subCategories.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Sub Category (Child) */}
          <div className="col-md-3">
            <label className="form-label">Sub Category (Child)</label>
            <select
              className="form-select form-select-sm"
              value={detailForm.SubCatChild || ""}
              onChange={handleChildChange}
            >
              <option value="">--Select Sub-Child--</option>
              {children.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* FAQ */}
          <div className="col-md-3">
            <label className="form-label">FAQ</label>
            <select
              className="form-select form-select-sm"
              disabled={!faqEnabled}
              value={detailForm.FAQ || ""}
              onChange={handleFaqChange}
            >
              <option value="">--Select--</option>
              {faqs.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.question}
                </option>
              ))}
            </select>
          </div>

          {/* Referred To + Short Query + Priority */}
          <div className="col-12 d-flex align-items-center gap-3 mt-1">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="isReferred"
                name="IsReferred"
                checked={!!detailForm.IsReferred}
                onChange={handleInputChange}
              />
              <label className="form-check-label ms-1" htmlFor="isReferred">
                Referred To
              </label>
            </div>

            <div style={{ minWidth: 260 }}>
              <select
                name="SelectedAgent"
                value={detailForm.SelectedAgent || ""}
                onChange={handleInputChange}
                className="form-select form-select-sm"
                disabled={!detailForm.IsReferred}
              >
                <option value="">--Select Agent To Transfer Query--</option>
                {agents.map((agent, idx) => (
                  <option key={idx} value={agent.FullName}>
                    {agent.FullName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-check ms-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="shortQuery"
                name="ShortQuery"
                checked={!!detailForm.ShortQuery}
                onChange={handleInputChange}
              />
              <label className="form-check-label ms-1" htmlFor="shortQuery">
                Short Query
              </label>
            </div>

            <div className="ms-auto d-flex align-items-center">
              <label className="form-label me-2 mb-0">Priority:</label>
              <select
                name="Priority"
                value={detailForm.Priority || "Normal"}
                onChange={handleInputChange}
                className="form-select form-select-sm"
                style={{ minWidth: 120 }}
              >
                <option>High</option>
                <option>Normal</option>
                <option>Low</option>
              </select>
            </div>
          </div>

          {/* Source + Further Help + Sindh Gov Dept */}
          <div className="col-12 d-flex align-items-center gap-3 mt-1">
            <div className="d-flex align-items-center" style={{ minWidth: 320 }}>
              <label className="form-label me-2 mb-0">Source</label>
              <select
                name="ComplaintSource"
                value={detailForm.ComplaintSource || ""}
                onChange={handleInputChange}
                className="form-select form-select-sm bg-warning fw-bold"
                style={{ minWidth: 200 }}
              >
                <option value="">--Select--</option>
                <option>Incoming call</option>
                <option>Outbound call</option>
                <option>Voicemail</option>
                <option>Email</option>
              </select>
            </div>

            <div className="d-flex align-items-center flex-grow-1">
              <label className="form-label me-2 mb-0">Further Help:</label>
              <input
                type="text"
                name="FurtherHelp"
                value={detailForm.FurtherHelp || ""}
                onChange={handleInputChange}
                className="form-control form-control-sm"
              />
            </div>

            <div className="d-flex align-items-center" style={{ minWidth: 360 }}>
              <label className="form-label me-2 mb-0">
                Sindh Gov Department
              </label>
              <select
                name="SindhGovDept"
                value={detailForm.SindhGovDept || ""}
                onChange={handleInputChange}
                className="form-select form-select-sm bg-warning fw-bold"
                style={{ minWidth: 200 }}
              >
                {sindhGovDepartments.map((dept, index) => (
                  <option key={index} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
              </div>
            </div>

            {/* Flags row (legacy WinForms toggles) */}
            <div className="col-12 d-flex flex-wrap align-items-center gap-3 mt-2">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="adrQuery"
                  name="ADRQuery"
                  checked={!!detailForm.ADRQuery}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ms-1" htmlFor="adrQuery">
                  ADR Query
                </label>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="covid"
                  name="COVID"
                  checked={!!detailForm.COVID}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ms-1" htmlFor="covid">
                  COVID-19
                </label>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="ageAboveFifty"
                  name="AgeAboveFifty"
                  checked={!!detailForm.AgeAboveFifty}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ms-1" htmlFor="ageAboveFifty">
                  Age &gt; 50
                </label>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="domesticViolence"
                  name="Domesticviolence"
                  checked={!!detailForm.Domesticviolence}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ms-1" htmlFor="domesticViolence">
                  Gender Base Violence
                </label>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="womenProperty"
                  name="WomenProperty"
                  checked={!!detailForm.WomenProperty}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ms-1" htmlFor="womenProperty">
                  Women Property
                </label>
              </div>
            </div>

            {/* Text areas */}
            <div className="col-md-6">
              <label className="form-label">Query</label>
              <textarea
                  name="Query"
              value={detailForm.Query || ""}
              onChange={handleInputChange}
              rows={2}
              className="form-control form-control-sm"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Solution</label>
            <textarea
              name="Solution"
              value={detailForm.Solution || ""}
              onChange={handleInputChange}
              rows={2}
              className="form-control form-control-sm"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">SMS Text</label>
            <select
              name="SMSText"
              value={detailForm.SMSText || ""}
              onChange={handleInputChange}
              className="form-control form-control-sm"
            >
              <option value="">--Select Service Referral--</option>
              {smsTextDropdownOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          {/* Main Tags (multi-select) */}
          <div className="col-md-3">
            <label className="form-label">Main Tags</label>
            <MultiCheckSelect
              label="Main Tags"
              placeholder="--Select Main Tags--"
              options={oldTagOptions.map(({ id, name }) => ({ id, name }))}
              checkedIds={
                Array.isArray(detailForm.OldTagIds)
                  ? detailForm.OldTagIds.map(asKey)
                  : []
              }
              onToggle={(id) => toggleIdInArray("OldTagIds", id)}
            />
            {tagFieldErrors.mainTags && (
              <div className="text-danger small mt-1">{tagFieldErrors.mainTags}</div>
            )}
          </div>

          {/* MFL (multi-select) */}
          <div className="col-md-4">
            <label className="form-label">For MFL</label>
            <MultiCheckSelect
              label="MFL"
              placeholder="--Select MFL--"
              options={mflList.map(({ id, name }) => ({ id, name }))}
              checkedIds={
                Array.isArray(detailForm.MFLIds)
                  ? detailForm.MFLIds.map(asKey)
                  : []
              }
              onToggle={(id) => toggleIdInArray("MFLIds", id)}
            />
            {tagFieldErrors.forMfl && (
              <div className="text-danger small mt-1">{tagFieldErrors.forMfl}</div>
            )}
          </div>

          {/* WRLMP (multi-select) */}
          <div className="col-md-4">
            <label className="form-label">For WRLMP</label>
            <MultiCheckSelect
              label="WRLMP"
              placeholder="--Select WRLMP--"
              options={wrlmpList.map(({ id, name }) => ({ id, name }))}
              checkedIds={
                Array.isArray(detailForm.WRLMPIds)
                  ? detailForm.WRLMPIds.map(asKey)
                  : []
              }
              onToggle={(id) => toggleIdInArray("WRLMPIds", id)}
            />
            {tagFieldErrors.forWrlmp && (
              <div className="text-danger small mt-1">{tagFieldErrors.forWrlmp}</div>
            )}
          </div>

          {/* Actions */}
          <div className="col-12 text-end mt-3">
            <button className="btn btn-secondary btn-sm me-2" onClick={uncheckAll}>
              Uncheck All
            </button>

            <button
              className="btn btn-success btn-sm me-2"
              onClick={() => {
                alert("SMS feature coming soon.");
              }}
            >
              Send SMS
            </button>

            <button
              className="btn btn-primary btn-sm me-2"
              onClick={() => handleProceedClick("Initiated")}
              disabled={saving}
            >
              Process Initiate
            </button>

            <button
              className="btn btn-success btn-sm me-2"
              onClick={() => handleProceedClick("Complete")}
              disabled={saving || !canComplete}
              title={
                canComplete
                  ? "All key fields present: will create as Complete"
                  : "Missing Complete fields: add department, query, and solution to enable"
              }
            >
              Complete
            </button>

            <button
              className="btn btn-outline-primary btn-sm me-2"
              onClick={handleNewQuery}
            >
              New Query
            </button>

            <button
              className="btn btn-success btn-sm me-2"
              onClick={() => {
                alert("View Notes coming soon.");
              }}
            >
              View Notes
            </button>

            {Boolean(detailForm?.QueryID && !isNewQuery) && (
              <button
                className="btn btn-success btn-sm"
                onClick={handleSaveDetails}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
